<script setup lang="ts">
interface Props {
  portalId: string;
  formId: string;
  region?: string;
  targetId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  region: "eu1",
});

// useId() is stable across SSR + client — Math.random() in the old default
// caused mismatched ids if this ever rendered outside ClientOnly.
const resolvedTargetId = computed(() => props.targetId ?? `hubspot-form-${useId()}`);

const error = ref("");

onMounted(() => {
  if (!import.meta.client) return;

  const script = document.createElement("script");
  script.src = "https://js.hsforms.net/forms/embed/v2.js";
  script.async = true;
  script.onload = () => {
    if (window.hbspt) {
      window.hbspt.forms.create({
        region: props.region,
        portalId: props.portalId,
        formId: props.formId,
        target: `#${resolvedTargetId.value}`,
        // Country selector is hidden via CSS; lock the value to GB so any
        // intl-phone field submits with a UK dial code.
        onFormReady: ($form: HTMLFormElement) => {
          const root =
            $form instanceof HTMLElement
              ? $form
              : document.querySelector(`#${resolvedTargetId.value}`);
          const select = root?.querySelector<HTMLSelectElement>(".hs-fieldtype-intl-phone select");
          if (select && select.value !== "GB") {
            select.value = "GB";
            select.dispatchEvent(new Event("change", { bubbles: true }));
          }
        },
      });
    }
  };
  script.onerror = () => {
    error.value = "Failed to load form. Please try again.";
  };
  document.head.appendChild(script);
});
</script>

<template>
  <ClientOnly>
    <div
      :id="resolvedTargetId"
      class="hubspot-form"
    />
    <BannerError :message="error" />
  </ClientOnly>
</template>

<style scoped>
.hubspot-form {
  min-height: 200px;
}
</style>
